import { useEffect, useRef, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useToast } from '../../../contexts/ToastContext';
import { RequestDetail } from './CapacityBoard';
import { refreshCapacity, useCapacityPreview } from './model';

const responseLabels: Record<string, string> = {
  APPROVED: 'Approved', DENIED: 'Denied', COUNTERED: 'Counteroffer', NEEDS_INFO: 'Information requested', REPLY: 'New reply',
};
const storageKey = (email: string) => `capacity-response-toasts:${email}`;
function readSeen(email: string): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(storageKey(email));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return Object.fromEntries(Object.entries(parsed).filter(([key, value]) => /^\d+$/.test(key) && typeof value === 'number' && Number.isSafeInteger(value) && value >= 0));
  } catch { return null; }
}

/** Reuses the shared 30-second capacity feed on every authenticated app page. */
export default function CapacityResponseNotifications() {
  const { permissions } = usePermissions();
  const email = permissions?.user_email?.trim().toLowerCase() ?? '';
  const { requests, loading, error } = useCapacityPreview();
  const { showToast, dismissToast } = useToast();
  const cursor = useRef<{ email: string; seen: Record<string, number> | null }>({ email: '', seen: null });
  const raised = useRef(new Map<number, number>());
  const [selected, setSelected] = useState<{ email: string; id: number } | null>(null);

  useEffect(() => {
    const toasts = raised.current;
    const refresh = () => { void refreshCapacity(); };
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      for (const id of toasts.values()) dismissToast(id);
      toasts.clear();
    };
  }, [email, dismissToast]);

  useEffect(() => {
    if (!email || loading || error) return;
    if (cursor.current.email !== email) cursor.current = { email, seen: readSeen(email) };
    const initial = cursor.current.seen === null;
    const seen = cursor.current.seen ?? {};
    for (const request of requests) {
      if (request.requester.trim().toLowerCase() !== email || !request.events?.length) continue;
      const previous = seen[request.id] ?? 0;
      // Coalesce several responses between polls to the latest for this request.
      const response = [...request.events].reverse().find(event => event.id > previous
        && event.actor.trim().toLowerCase() !== email
        && /^(APPROVED|DENIED|COUNTERED|NEEDS_INFO|REPLY)(?: · [^:]+)?:/.test(event.message));
      seen[request.id] = Math.max(previous, ...request.events.map(event => event.id));
      if (initial || !response) continue;
      const action = response.message.split(/[ :·]/)[0];
      const oldToast = raised.current.get(request.id);
      if (oldToast !== undefined) dismissToast(oldToast);
      const id = showToast({
        message: `Capacity request #${request.id}${request.pro ? ` · PRO ${request.pro}` : ` · ${request.customer}`} — ${responseLabels[action]} from ${response.actor}`,
        type: action === 'APPROVED' ? 'success' : action === 'DENIED' ? 'warning' : 'info',
        duration: 0,
        onClick: () => setSelected({ email, id: request.id }),
      });
      raised.current.set(request.id, id);
    }
    // The first successful load establishes a baseline rather than replaying
    // historical decisions. Subsequent visits catch responses since the last visit.
    cursor.current.seen = seen;
    try { localStorage.setItem(storageKey(email), JSON.stringify(seen)); } catch { /* Session deduplication still works when storage is unavailable. */ }
  }, [email, requests, loading, error, showToast, dismissToast]);

  return selected?.email === email ? <RequestDetail key={`${email}:${selected.id}`} id={selected.id} onClose={() => setSelected(null)} /> : null;
}
